// TalkMeDownCard — the Today-screen card that opens AI chat on tap and
// jumps straight to the CrisisModal on a 600ms long-press OR on the
// explicit "Crisis resources" button (the keyboard / screen-reader /
// switch-user path).
//
// The long-press shortcut is touch-discovery sugar; the visible
// button is the canonical path. WCAG 2.2 SC 2.5.1 — every path-based
// or timed pointer gesture must have a single-pointer / keyboard
// alternative, and the caption used to promise a feature that didn't
// exist for non-touch users.
//
// Cancel on pointer-move > 8px so a vertical scroll doesn't trigger
// the crisis path by accident. We also clear the timer on
// pointerleave / pointercancel.

import { useRef } from 'react';
import { LifeBuoy, MessageCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { haptics } from '../../lib/haptics';

const LONG_PRESS_MS = 600;
const MOVE_CANCEL_PX = 8;

interface TalkMeDownCardProps {
  onOpenChat: () => void;
  onLongPress: () => void;
}

export function TalkMeDownCard({ onOpenChat, onLongPress }: TalkMeDownCardProps) {
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  function clearTimer() {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    firedRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      firedRef.current = true;
      haptics.warn();
      onLongPress();
    }, LONG_PRESS_MS);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!startRef.current || !timerRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) {
      clearTimer();
    }
  }

  function onPointerUp() {
    clearTimer();
    startRef.current = null;
  }

  function onPointerCancel() {
    clearTimer();
    startRef.current = null;
  }

  return (
    <Card
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerCancel}
      style={{ touchAction: 'manipulation' }}
    >
      <div className="row" style={{ gap: 'var(--space-3)', alignItems: 'flex-start' }}>
        <span
          className="crisis-action-icon"
          style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}
        >
          <Icon icon={MessageCircle} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Talk me down</div>
          <div className="muted text-caption">
            A private chat when a craving hits. Tap <strong>Crisis resources</strong>{' '}
            for 988 and live help.
          </div>
          <div
            className="row"
            style={{ gap: 'var(--space-2)', marginBlockStart: 'var(--space-3)', flexWrap: 'wrap' }}
          >
            <Button
              variant="tertiary"
onClick={(e) => {
                // Stop the click from also triggering the card's
                // pointerdown handler (it doesn't, since pointerdown
                // already fired by this point, but be defensive).
                e.stopPropagation();
                onLongPress();
              }}
              aria-label="Open crisis resources — 988, Crisis Text Line, SAMHSA, 911"
            >
              <Icon icon={LifeBuoy} size={14} /> Crisis resources
            </Button>
            <Button
              variant="accent"
onClick={(e) => {
                e.stopPropagation();
                // If the long-press already fired, suppress the tap.
                if (firedRef.current) return;
                onOpenChat();
              }}
            >
              Open chat
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
